/* Hybrid stack - ambient slot cycling for the "Our hybrid approach" card.
   Vanilla, no dependencies. Public API: HybridStack.init(el, opts) / .destroy(el).
   Including this file twice is a no-op the second time. */
(function () {
  'use strict';
  if (window.HybridStack) return;

  var DEFAULTS = { interval: 2400, duration: 320, swap: 2 };
  var LIMITS = { interval: [800, 20000], duration: [100, 2000], swap: [1, 4] };
  var OUT_MS = 260;   // lift-out, also the handoff delay: the DOM is written when it ends
  /* each further slot in a swap starts as the one before it is finishing: its
     lift begins this much before the previous slot's settle ends, so the two
     read as separate events that just touch */
  var PAIR_OVERLAP = 100;
  /* share of the interval before the first swap after the card enters the
     viewport: the rows and the footer have just finished settling in (ENTER_MS
     + 4 stagger steps = 780ms), so the first swap lands inside the first second
     of attention instead of a full interval of stillness */
  var FIRST_WAIT = 0.45;
  var ENTER_MS = 420;       // each row's rise-in
  var ENTER_STAGGER = 90;   // top to bottom, the footer last
  var LEGEND_MS = 600;
  var JITTER = 0.15;

  /* Fallback pool - identical to the JSON block the markup ships, used when that
     block is missing, unparseable or fails validation. */
  var FALLBACK_POOL = {
    tiers: [
      { name: 'platform', items: [
        { label: 'Supermicro chassis', new: 0.75 },
        { label: 'Gigabyte GPU barebone', new: 0.6 },
        { label: 'HPE DL380 Gen11 chassis', new: 0.5 },
        { label: 'ASUS ESC8000 GPU server', new: 0.3 },
        { label: 'Supermicro 4-node TwinPro', new: 0.4 }
      ] },
      { name: 'compute', items: [
        { label: 'Certified compute nodes', new: 0 },
        { label: 'NVIDIA H100 GPUs', new: 0.6 },
        { label: 'AMD EPYC 9004 CPUs', new: 0.3 },
        { label: 'Intel Xeon Scalable CPUs', new: 0.15 },
        { label: 'NVIDIA A100 SXM baseboard', new: 0.2 },
        { label: 'Gigabyte compute nodes', new: 0.5 }
      ] },
      { name: 'storage', items: [
        { label: 'Storage and drive trays', new: 0.5 },
        { label: 'Samsung NVMe drives', new: 0.6 },
        { label: 'DDR5 memory kits', new: 0.5 },
        { label: 'SAS drives and backplanes', new: 0.2 },
        { label: 'Micron M.2 boot drives', new: 0.7 }
      ] },
      { name: 'network', items: [
        { label: 'Switching and optics', new: 0.3 },
        { label: 'Mellanox ConnectX NICs', new: 0.3 },
        { label: 'Arista 100G switching', new: 0.15 },
        { label: 'Cisco Nexus switching', new: 0.15 },
        { label: 'Redundant power supplies', new: 0.5 },
        { label: 'Rail kits and risers', new: 0.6 }
      ] }
    ]
  };

  var instances = [];
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  /* Number(), not parseFloat(): "2400px" is a typo, not a 2400ms setting, and
     has to fall back rather than half-parse. */
  function num(raw, key) {
    var v = Number(raw);
    if (!isFinite(v) || v < LIMITS[key][0] || v > LIMITS[key][1]) return DEFAULTS[key];
    return v;
  }

  /* Returns the reason the pool is unusable, or null. Every tier a slot asks for
     must exist with two labels to alternate between, no repeats (a repeat would
     let a slot "swap" to what it already shows) and a drawable weight. */
  function poolProblem(pool, tierNames) {
    if (!pool || !Array.isArray(pool.tiers)) return 'the pool has no tiers array';
    /* every entry must be a tier object with a string name: a null or primitive
       entry is a template bug, and silently skipping it would hide that bug
       behind an otherwise valid pool. */
    for (var e = 0; e < pool.tiers.length; e++) {
      var entry = pool.tiers[e];
      if (!entry || typeof entry !== 'object' || typeof entry.name !== 'string') return 'tiers[' + e + '] is not a tier object';
    }
    for (var t = 0; t < tierNames.length; t++) {
      var name = tierNames[t], tier = null;
      for (var i = 0; i < pool.tiers.length; i++) {
        /* the first match wins, the same one tierFor() picks - keeping the
           last let a broken first copy validate a good second one. */
        var cand = pool.tiers[i];
        if (cand.name !== name) continue;
        if (tier) return 'the pool has two tiers named "' + name + '"';
        tier = cand;
      }
      if (!tier || !Array.isArray(tier.items)) return 'tier "' + name + '" is missing';
      if (tier.items.length < 2) return 'tier "' + name + '" needs 2 items, has ' + tier.items.length;
      var seen = Object.create(null); /* a plain {} would see 'constructor' as a repeat */
      for (var k = 0; k < tier.items.length; k++) {
        var item = tier.items[k];
        if (!item || typeof item.label !== 'string' || !item.label) return 'tier "' + name + '" item ' + k + ' has no label';
        if (seen[item.label]) return 'tier "' + name + '" repeats the label "' + item.label + '"';
        seen[item.label] = true;
        if (typeof item.new !== 'number' || !(item.new >= 0 && item.new <= 1)) {
          return 'tier "' + name + '" item "' + item.label + '" has no usable "new" weight (' + item.new + ')';
        }
      }
    }
    return null;
  }

  function readPool(el, tierNames) {
    var block = el.querySelector('script[type="application/json"][data-hybrid-stack-pool]');
    var pool = null, why = null;
    if (!block) why = 'no [data-hybrid-stack-pool] block';
    else {
      try { pool = JSON.parse(block.textContent); }
      catch (err) { why = 'the pool JSON is unparseable (' + err.message + ')'; }
    }
    /* the validator walks author-supplied JSON; anything it still trips over is
       a bad pool, not a reason for init to throw */
    if (!why) {
      try { why = poolProblem(pool, tierNames); }
      catch (err) { why = 'the pool could not be validated (' + err.message + ')'; }
    }
    if (why) {
      console.warn('HybridStack: ' + why + '; using the built-in pool.');
      return FALLBACK_POOL;
    }
    return pool;
  }

  function find(el) {
    for (var i = 0; i < instances.length; i++) if (instances[i].el === el) return i;
    return -1;
  }

  function Instance(el, opts) {
    opts = opts || {};
    this.el = el;
    this.opts = opts; /* kept so a media-query flip can re-read them, not drop them */
    this.random = typeof opts.random === 'function' ? opts.random : Math.random;
    this.interval = opts.interval != null ? num(opts.interval, 'interval') : num(el.getAttribute('data-interval'), 'interval');
    this.duration = opts.duration != null ? num(opts.duration, 'duration') : num(el.getAttribute('data-duration'), 'duration');
    this.swap = opts.swap != null ? num(opts.swap, 'swap') : num(el.getAttribute('data-swap'), 'swap');
    /* THEME PATCH (Core 4): the badge strings were hardcoded 'New' / 'Refurb'
       in paint(). The theme renders them from locales, so read them off the
       element and keep the package's words as the fallback. Re-apply this and
       the matching line in paint() if the package is ever re-exported. */
    this.labels = {
      'new': el.getAttribute('data-label-new') || 'New',
      refurb: el.getAttribute('data-label-refurb') || 'Refurb'
    };
    this.timer = null;
    this.observer = null;
    this.visible = false;
    this.entered = false;
    this.motions = [];
    this.legendTimers = {};

    this.legend = {};
    var items = el.querySelectorAll('[data-legend]');
    for (var i = 0; i < items.length; i++) this.legend[items[i].getAttribute('data-legend')] = items[i];

    var slotEls = el.querySelectorAll('.hybrid-stack__slot');
    var tierNames = [];
    for (var n = 0; n < slotEls.length; n++) tierNames.push(slotEls[n].getAttribute('data-tier'));
    this.pool = readPool(el, tierNames);

    this.slots = [];
    for (var s = 0; s < slotEls.length; s++) {
      var row = slotEls[s].querySelector('.hybrid-stack__row');
      var part = row && (row.querySelector('.hybrid-stack__part') || row);
      var badge = part && part.querySelector('.hybrid-stack__badge');
      var labelEl = part && part.querySelector('.hybrid-stack__label');
      /* a half-written slot is skipped with one warning; the rest of the card
         still cycles, which is what the .__part fallback above already assumed */
      if (!row || !badge || !labelEl) {
        console.warn('HybridStack: slot ' + s + ' has no .hybrid-stack__row, __badge or __label; skipping it.');
        continue;
      }
      this.slots.push({
        el: slotEls[s],
        part: part,
        badge: badge,
        labelEl: labelEl,
        tier: this.tierFor(tierNames[s]),
        label: labelEl.textContent.trim(),
        isNew: badge.classList.contains('is-new'),
        initial: { label: labelEl.textContent.trim(), isNew: badge.classList.contains('is-new'), html: slotEls[s].innerHTML },
        timer: null,
        swapping: false
      });
    }

    this.swap = Math.min(Math.floor(this.swap), this.slots.length);
    this.order = [];
    this.last = [];
    this.reduced = !!(reduceQuery && reduceQuery.matches);

    /* the rows are hidden from the first paint until the card is in view; the
       class is set here, synchronously, so there is no frame of visible rows
       before the observer's first delivery hides them again. Never under
       reduced motion, and dropped again on every path out of the instance. */
    if (this.slots.length && !this.reduced) el.classList.add('is-entering');
    if (this.slots.length) this.observe();
  }

  Instance.prototype.tierFor = function (name) {
    for (var i = 0; i < this.pool.tiers.length; i++) {
      var cand = this.pool.tiers[i];
      if (cand && typeof cand === 'object' && cand.name === name) return cand;
    }
    return { name: name, items: [] };
  };

  /* --- scheduling ------------------------------------------------------- */

  Instance.prototype.observe = function () {
    var self = this;
    if (!window.IntersectionObserver) { this.visible = true; this.sync(); return; }
    this.observer = new IntersectionObserver(function (entries) {
      self.visible = entries[entries.length - 1].isIntersecting;
      self.sync();
    });
    this.observer.observe(this.el);
  };

  Instance.prototype.running = function () {
    return !this.reduced && this.visible && !document.hidden;
  };

  Instance.prototype.sync = function () {
    if (this.running()) { this.enter(); this.arm(); }
    /* clearLegend() too: a flash left mid-transition would outlive the pause and
       re-fire its timer into a card that is no longer moving */
    else { this.disarm(); this.settleAll(); this.clearLegend(); }
  };

  Instance.prototype.arm = function () {
    if (this.timer || !this.running()) return;
    var self = this;
    /* the floor keeps "one batch at a time" true even when duration outruns
       the interval, e.g. interval 800 with duration 2000 */
    var share = this.primed ? 1 + (this.random() * 2 - 1) * JITTER : FIRST_WAIT;
    var wait = Math.max(this.interval * share, OUT_MS + this.duration + this.stagger() * (this.swap - 1));
    /* the first swap also waits for the footer, the last beat, to finish rising in */
    if (!this.primed) wait = Math.max(wait, ENTER_MS + ENTER_STAGGER * this.slots.length);
    this.timer = setTimeout(function () {
      self.timer = null;
      self.primed = true;
      self.step(true);
      self.arm();
    }, wait);
  };

  Instance.prototype.disarm = function () {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  };

  /* one slot's whole swap minus the overlap; 480ms at the defaults */
  Instance.prototype.stagger = function () {
    return OUT_MS + this.duration - PAIR_OVERLAP;
  };

  /* --- entrance --------------------------------------------------------- */

  /* Once per instance, on the first visible tick: the rows rise in top to
     bottom, the footer last, then the cycle starts. The class comes off first so a browser with
     no animate() simply shows the rows. Backwards fill only: a delayed row
     stays hidden until its turn, and a cancelled entrance (pause mid-rise)
     leaves the rows at their resting state, not hidden. */
  Instance.prototype.enter = function () {
    if (this.entered) return;
    this.entered = true;
    this.el.classList.remove('is-entering');
    var beats = [];
    for (var i = 0; i < this.slots.length; i++) beats.push(this.slots[i].el.querySelector('.hybrid-stack__row'));
    beats.push(this.el.querySelector('.hybrid-stack__footer'));
    for (var b = 0; b < beats.length; b++) {
      if (!beats[b] || !beats[b].animate) continue;
      this.track(beats[b].animate(
        [{ transform: 'translateY(10px)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
        { duration: ENTER_MS, delay: b * ENTER_STAGGER, easing: 'ease-out', fill: 'backwards' }
      ));
    }
  };

  /* --- swap logic ------------------------------------------------------- */

  /* the next `swap` slots off a permutation of all of them, refilled when it
     runs dry. The refill is rejected while its first batch is the batch that
     just moved, so the same rows never swap twice running while the rest sit
     still; for swap 1 that is the old "no slot twice in a row". */
  Instance.prototype.nextSlots = function () {
    var n = this.slots.length, batch = [];
    while (batch.length < this.swap) {
      if (!this.order.length) {
        do {
          this.order = [];
          for (var i = 0; i < n; i++) this.order.push(i);
          for (var j = n - 1; j > 0; j--) {
            var k = Math.floor(this.random() * (j + 1));
            var t = this.order[j]; this.order[j] = this.order[k]; this.order[k] = t;
          }
        } while (n > this.swap && this.sameBatch(this.order.slice(0, this.swap)));
      }
      batch.push(this.order.shift());
    }
    this.last = batch;
    return batch;
  };

  Instance.prototype.sameBatch = function (next) {
    if (next.length !== this.last.length) return false;
    for (var i = 0; i < next.length; i++) if (this.last.indexOf(next[i]) === -1) return false;
    return true;
  };

  /* One swap: `swap` slots move together. Each pick is decided - and stored on
     its slot - here, at the start; the DOM catches up at the handoff, or
     immediately if the swap is cancelled. Returns the picks, which is what
     selftest.html asserts on. `animate` is false in the self-test so 5000 swaps
     cost no animations. */
  Instance.prototype.step = function (animate) {
    var batch = this.nextSlots();
    var picks = [];
    for (var b = 0; b < batch.length; b++) {
      var index = batch[b];
      var slot = this.slots[index];
      var items = slot.tier.items.filter(function (it) { return it.label !== slot.label; });
      if (!items.length) return null;
      var part = items[Math.floor(this.random() * items.length)];

      /* invariant 1: keep at least one NEW and one REFURB among the four rows.
         Only the batch's last pick is ever forced: the slots still to be
         decided after this one can restore the mix, so forcing here would bend
         the weights for nothing. "others" is every slot outside the batch plus
         the batch members already decided. */
      var others = 0, newOthers = 0;
      for (var i = 0; i < this.slots.length; i++) {
        var pos = batch.indexOf(i);
        if (pos === b || pos > b) continue;
        others++;
        if (this.slots[i].isNew) newOthers++;
      }
      var forced = null;
      if (b === batch.length - 1) {
        if (others && newOthers === others) forced = false;
        else if (others && newOthers === 0) forced = true;
      }
      var isNew = forced !== null ? forced : this.random() < part.new;

      slot.label = part.label;
      slot.isNew = isNew;
      picks.push({ slot: index, tier: slot.tier.name, label: part.label, isNew: isNew, forced: forced !== null });
    }
    for (var p = 0; p < picks.length; p++) {
      var moved = this.slots[picks[p].slot];
      if (animate === false || !moved.part.animate) this.paint(moved);
      else {
        /* the scheduler's floor means the previous swap has just ended; retire
           it from state so two batches are never counted - or drawn - mid-swap */
        if (p === 0) this.settleAll();
        this.lift(moved, this.stagger() * p);
      }
    }
    return picks;
  };

  /* the only DOM write: state -> row contents */
  Instance.prototype.paint = function (slot) {
    slot.labelEl.textContent = slot.label;
    slot.badge.classList.toggle('is-new', slot.isNew);
    slot.badge.textContent = slot.isNew ? this.labels['new'] : this.labels.refurb;
  };

  Instance.prototype.track = function (anim, slot) {
    if (anim) { anim.hsSlot = slot || null; this.motions.push(anim); }
  };

  /* with a slot: only that slot's motions, so one slot's handoff never cancels
     the other's still-running lift-out; without: everything */
  Instance.prototype.clearMotions = function (slot) {
    var keep = [];
    for (var i = 0; i < this.motions.length; i++) {
      if (slot && this.motions[i].hsSlot !== slot) keep.push(this.motions[i]);
      else this.motions[i].cancel();
    }
    this.motions = keep;
  };

  /* phase 1: the row's contents leave. The box itself never moves, so nothing
     composites over anything else and no second row exists to clean up. `delay`
     holds the second slot of a pair until the first is nearly settled; fill
     'both' keeps its contents in place until its turn. */
  Instance.prototype.lift = function (slot, delay) {
    var self = this;
    slot.swapping = true;
    this.track(slot.part.animate(
      [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-8px)', opacity: 0 }],
      { duration: OUT_MS, delay: delay || 0, easing: 'ease-in', fill: 'both' }
    ), slot);
    /* a timer, not onfinish: onfinish never fires on cancel, so cleanup hung on
       it leaks - this one is cleared on every pause path */
    slot.timer = setTimeout(function () { slot.timer = null; self.settle(slot); }, OUT_MS + (delay || 0));
  };

  /* phase 2: the handoff. Write the DOM, then bring the contents back. */
  Instance.prototype.settle = function (slot) {
    var self = this;
    this.clearMotions(slot); // the out animation holds a fill; drop it first
    this.paint(slot);
    this.track(slot.part.animate(
      [{ transform: 'translateY(12px)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
      { duration: this.duration, easing: 'ease-out' }
    ), slot);
    this.track(slot.badge.animate(
      /* runs over the whole settle, not a fixed 240ms: a shorter pop is spent
         while the badge is still under 60% opacity and reads as a plain fade */
      [{ transform: 'scale(.82)' }, { transform: 'scale(1)' }],
      { duration: this.duration, easing: 'ease-out' }
    ), slot);
    this.lightLegend(slot.isNew ? 'new' : 'refurb');
    slot.timer = setTimeout(function () { slot.timer = null; slot.swapping = false; }, this.duration);
  };

  /* Any pause lands here: the slot's turn was already spent picking, so write
     the pick out with no animation rather than dropping it. */
  Instance.prototype.settleAll = function () {
    this.clearMotions();
    for (var i = 0; i < this.slots.length; i++) {
      var slot = this.slots[i];
      if (slot.timer) { clearTimeout(slot.timer); slot.timer = null; }
      if (slot.swapping) { slot.swapping = false; this.paint(slot); }
    }
  };

  Instance.prototype.lightLegend = function (key) {
    var item = this.legend[key];
    if (!item) return;
    item.classList.add('is-lit');
    /* one timer per key, re-armed: a shared array grew without bound */
    clearTimeout(this.legendTimers[key]);
    this.legendTimers[key] = setTimeout(function () { item.classList.remove('is-lit'); }, LEGEND_MS);
  };

  Instance.prototype.clearLegend = function () {
    for (var key in this.legendTimers) clearTimeout(this.legendTimers[key]);
    this.legendTimers = {};
    for (var lit in this.legend) this.legend[lit].classList.remove('is-lit');
  };

  /* reduced motion is a reset, not a pause: the card goes back to the state the
     markup shipped and stays there. */
  Instance.prototype.restore = function () {
    for (var i = 0; i < this.slots.length; i++) {
      var slot = this.slots[i];
      slot.label = slot.initial.label;
      slot.isNew = slot.initial.isNew;
      this.paint(slot);
    }
    this.order = [];
    this.last = [];
  };

  Instance.prototype.setReduced = function (reduced) {
    this.reduced = !!reduced;
    if (this.reduced) { this.entered = true; this.el.classList.remove('is-entering'); }
    this.disarm();
    this.settleAll();
    if (this.reduced) this.restore();
    this.sync();
  };

  Instance.prototype.destroy = function () {
    this.disarm();
    this.settleAll();
    this.clearLegend();
    this.el.classList.remove('is-entering');
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    /* only our own nodes: a subtree sweep also killed the host's keyframes, e.g.
       a theme animating .hybrid-stack__scrim. settleAll() dropped the tracked
       motions; these two catch anything the browser still holds on them. */
    for (var m = 0; m < this.slots.length; m++) {
      var slot = this.slots[m];
      if (slot.part.getAnimations) slot.part.getAnimations().forEach(function (a) { a.cancel(); });
      if (slot.badge.getAnimations) slot.badge.getAnimations().forEach(function (a) { a.cancel(); });
    }
    for (var i = 0; i < this.slots.length; i++) this.slots[i].el.innerHTML = this.slots[i].initial.html;
    this.slots = [];
  };

  /* --- page-level wiring ------------------------------------------------ */

  function onVisibility() {
    for (var i = 0; i < instances.length; i++) instances[i].sync();
  }

  /* re-point the live instances instead of re-initialising them: init() with no
     opts would drop each instance's interval, duration and random */
  function onReduceChange() {
    var reduced = !!(reduceQuery && reduceQuery.matches);
    for (var i = 0; i < instances.length; i++) instances[i].setReduced(reduced);
  }

  document.addEventListener('visibilitychange', onVisibility);
  if (reduceQuery) {
    if (reduceQuery.addEventListener) reduceQuery.addEventListener('change', onReduceChange);
    else if (reduceQuery.addListener) reduceQuery.addListener(onReduceChange);
  }

  var HybridStack = {
    init: function (el, opts) {
      if (!el) return null;
      HybridStack.destroy(el);
      var inst = new Instance(el, opts);
      instances.push(inst);
      return inst;
    },
    destroy: function (el) {
      var i = find(el);
      if (i === -1) return;
      instances[i].destroy();
      instances.splice(i, 1);
    }
  };

  function autoInit() {
    var all = document.querySelectorAll('[data-hybrid-stack]');
    for (var i = 0; i < all.length; i++) {
      if (find(all[i]) !== -1) continue;
      /* per element: a card that throws out of init must not take the rest of
         the page's cards down with it */
      try { HybridStack.init(all[i]); }
      catch (err) { console.error('HybridStack: init failed on', all[i], err && err.message); }
    }
  }

  window.HybridStack = HybridStack;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
  else autoInit();
})();
